import * as React from "react";
import { ClubAllowedSectionsApi, SaveNewClubAllowedSectionDto } from "../../../../../api";
import { SmallFormGenerator } from "../../../../../components";
import { useApiService, useSaveApi, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { useReferential } from "../../../../../hooks/useReferential";
import { FGSelectInput, FGTextInput, useApiEffect } from "nsitools-react";
import * as Yup from "yup";
import { useParams } from "react-router";
import { Spinner } from "@blueprintjs/core";

export interface IClubAllowedSectionsSelectorPanelProps {
  clubId: number;
  clubSectionId: number;
  onSaved: () => void;
}

export const ClubAllowedSectionsSelectorPanel: React.FunctionComponent<IClubAllowedSectionsSelectorPanelProps> = ({
  clubId,
  clubSectionId = 0,
  onSaved,
}) => {
  const api = useApiService(ClubAllowedSectionsApi);

  const [knownSection, loading] = useApiEffect(() => api.getClubAllowedSection(clubSectionId));

  const { t } = useTl();
  const { saving, saveItem } = useSaveApi<SaveNewClubAllowedSectionDto>(
    React.useMemo(
      () => ({
        saveApiFn: (data) => api.addNewClubAllowedSection(data),
        onSaveFn: () => onSaved(),
      }),
      [api, onSaved]
    )
  );

  const { id } = useParams<{ id: string }>();
  const [sections, sloading] = useReferential(
    (a) => (clubSectionId !== 0 ? a.getSectionsForClub(+id) : a.getSections()),
    false,
    []
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      sectionId: Yup.number().nullable().required(t(ETLCodes.Required)),
      contactEmails: Yup.string()
        .nullable()
        .matches(new RegExp("^.+@.+\\.\\w+(?:;.+@.+\\.\\w+)*$"), t(ETLCodes.MustBeValidSeparatedEmail)),
    });
  }, [t]);

  const initialData = React.useMemo(
    () => (clubSectionId !== 0 ? knownSection : new SaveNewClubAllowedSectionDto({ clubId: clubId }).toJSON()),
    [clubId, clubSectionId, knownSection]
  );

  return loading ? (
    <Spinner />
  ) : (
    <SmallFormGenerator
      onSubmit={saveItem}
      initialValues={initialData}
      editMode={true}
      validationSchema={FormSchema}
      saving={saving}
    >
      <FGSelectInput
        name="sectionId"
        items={sections}
        loading={sloading}
        label={t(ETLCodes.Section)}
        readonly={clubSectionId !== 0}
      />
      <FGTextInput name="contactEmails" label={t(ETLCodes.ContactEmails)} />
    </SmallFormGenerator>
  );
};
